import { uniqBy } from "lodash";
import {
  languageDisplayNames,
  technologyDisplayNames,
  focusDisplayNames,
} from "sj-ui-toolkit/src/constants/content";
import type { TagsData } from "@/api/tournaments";

// ─── Language normalisation ───────────────────────────────────────────────────

const LANGUAGE_VARIANTS: Record<string, string[]> = {
  cpp: ["c++", "cpp", "cplusplus"],
  "c++": ["c++", "cpp", "cplusplus"],
  cplusplus: ["c++", "cpp", "cplusplus"],
  dotnet: ["dotnet", "csharp", "c#"],
  "c#": ["dotnet", "csharp", "c#"],
  csharp: ["dotnet", "csharp", "c#"],
};

const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  cpp: "cpp",
  "c++": "cpp",
  cplusplus: "cpp",
  dotnet: "dotnet",
  "c#": "dotnet",
  csharp: "dotnet",
};

const normalizeLanguageName = (lang: string): string =>
  LANGUAGE_DISPLAY_NAMES[lang.toLowerCase()] ?? lang;

const getLanguageDisplayName = (lang: string): string => {
  const normalized = normalizeLanguageName(lang);
  return (languageDisplayNames as Record<string, string>)[normalized] ?? normalized;
};

const getTechnologyDisplayName = (tech: string): string =>
  (technologyDisplayNames as Record<string, string>)[tech.toLowerCase()] ?? tech;

const getFocusDisplayName = (focus: string): string =>
  (focusDisplayNames as Record<string, string>)[focus.toLowerCase()] ?? focus;

export const expandLanguageVariants = (languages: string[] = []): string[] =>
  uniqBy(
    languages.flatMap(lang => LANGUAGE_VARIANTS[lang] ?? [lang]),
    (v: string) => v.toLowerCase()
  );

// ─── Filter option builders ───────────────────────────────────────────────────

export interface FilterOption {
  name: string;
  value: string;
}

export interface FilterGroup {
  title: string;
  label: string;
  filters: FilterOption[];
}

export const setLanguages = ({
  experimentLanguagesList = [],
  lessonsTagsData = {},
}: {
  experimentLanguagesList?: Array<{ name: string }>;
  lessonsTagsData?: TagsData;
}): FilterOption[] => {
  let languages: FilterOption[] = experimentLanguagesList
    .filter(e => e.name)
    .map(e => ({
      name: getLanguageDisplayName(e.name),
      value: normalizeLanguageName(e.name),
    }));

  if (lessonsTagsData.tagsCategories) {
    const lessonsLanguageItem = lessonsTagsData.tagsCategories.find(c => c.name === "language");
    if (lessonsLanguageItem) {
      const lessonLanguages = lessonsLanguageItem.tags
        .filter(t => t.name)
        .map(t => ({
          name: getLanguageDisplayName(t.name),
          value: normalizeLanguageName(t.name),
        }));
      languages = [...languages, ...lessonLanguages];
    }
  }

  return uniqBy(languages, "value").sort((a, b) => a.name.localeCompare(b.name));
};

export const setTechnologies = ({
  lessonsTagsData = {},
}: {
  lessonsTagsData?: TagsData;
}): FilterOption[] => {
  if (!lessonsTagsData.tagsCategories) return [];
  const item = lessonsTagsData.tagsCategories.find(c => c.name === "technology");
  if (!item) return [];
  return uniqBy(
    item.tags
      .filter(t => t.name)
      .map(t => ({
        name: getTechnologyDisplayName(t.name),
        value: t.name.toLowerCase(),
      })),
    "value"
  ).sort((a, b) => a.name.localeCompare(b.name));
};

export const setFocus = ({
  lessonsTagsData = {},
}: {
  lessonsTagsData?: TagsData;
}): FilterOption[] => {
  if (!lessonsTagsData.tagsCategories) return [];
  const item = lessonsTagsData.tagsCategories.find(c => c.name === "focus");
  if (!item) return [];
  return uniqBy(
    item.tags
      .filter(t => t.name)
      .map(t => ({
        name: getFocusDisplayName(t.name),
        value: t.name.toLowerCase(),
      })),
    "value"
  ).sort((a, b) => a.name.localeCompare(b.name));
};

const DIFFICULTY_OPTIONS: FilterOption[] = [
  { value: "awareness", name: "Security Awareness" },
  { value: "basic", name: "Basic" },
  { value: "intermediate", name: "Intermediate" },
  { value: "advanced", name: "Advanced" },
];

export const setLevel = ({
  experimentDifficultiesList = [],
}: {
  experimentDifficultiesList?: Array<{ name: string }>;
}): FilterOption[] => {
  const experimentsOptions = experimentDifficultiesList
    .filter(e => e.name)
    .map(e => ({ name: e.name, value: e.name }));
  return uniqBy([...experimentsOptions, ...DIFFICULTY_OPTIONS], "value");
};

const ACTIVITY_TYPE_OPTIONS: FilterOption[] = [
  { value: "hackedu", name: "Break/Fix" },
  { value: "only_video", name: "Video Only" },
  { value: "with_experiments", name: "Video with Coding Exercises" },
  { value: "code_challenge", name: "Coding Challenge" },
  { value: "hacking_challenge", name: "CTF Challenge" },
  { value: "experiment_only", name: "Coding Exercises" },
  { value: "app_lab", name: "AI Skills Lab" },
];

export const setActivityType = (): FilterOption[] => ACTIVITY_TYPE_OPTIONS;

const RELEASE_OPTIONS: FilterOption[] = [{ value: "recently", name: "Recently Released" }];

export const setRelease = (): FilterOption[] => RELEASE_OPTIONS;

export const buildFilterOptions = (filterData: {
  lessonsTagsData: TagsData;
  experimentLanguagesList: Array<{ name: string }>;
  experimentDifficultiesList: Array<{ name: string }>;
}): FilterGroup[] => [
  {
    title: "Language",
    label: "language",
    filters: setLanguages({
      experimentLanguagesList: filterData.experimentLanguagesList,
      lessonsTagsData: filterData.lessonsTagsData,
    }),
  },
  {
    title: "Technology",
    label: "technology",
    filters: setTechnologies({ lessonsTagsData: filterData.lessonsTagsData }),
  },
  {
    title: "Role",
    label: "role",
    filters: setFocus({ lessonsTagsData: filterData.lessonsTagsData }),
  },
  {
    title: "Level",
    label: "level",
    filters: setLevel({ experimentDifficultiesList: filterData.experimentDifficultiesList }),
  },
  {
    title: "Activity Type",
    label: "activityType",
    filters: setActivityType(),
  },
  {
    title: "Release",
    label: "release",
    filters: setRelease(),
  },
];
